import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { StyleSheet, View } from 'react-native';

import { BgLandscapePng, BgPortraitPng } from '../../assets';
import { useDevice } from '../../utils/hooks/useDevice';
import { useSafeViewport } from '../../utils/hooks/useSafeViewport';
import { getNavRowHeight } from '../Components/AppFooter/Nav/FooterNavButtonsV2';
import { IndicatorScrollView } from '../Components/common/ScrollIndicator';
import { getDesktopColumns } from '../Components/Desktop/DesktopBottomBars';
import { desktopScrollFade, useDesktopLayout } from '../Components/Desktop/desktopLayout';
import EventHeroBanner from '../Components/Event/EventHeroBanner';
import EventInfoPanel from '../Components/Event/EventInfoPanel';
import { createPlaceholderEvent, EventTrack, formatCountdown } from '../Components/Event/eventMock';
import EventPointsCard from '../Components/Event/EventPointsCard';
import EventScrollFade from '../Components/Event/EventScrollFade';
import EventTabs, { EventTab } from '../Components/Event/EventTabs';
import {
	DESIGN_CONTENT_WIDTH,
	DESIGN_FRAME_WIDTH,
	DESIGN_SECTION_GAP,
	MAX_SCALE,
} from '../Components/Event/eventTheme';
import EventTrackCard from '../Components/Event/EventTrackCard';
import GreenButton from '../Components/Event/GreenButton';
import UnlockPremiumBanner, { UNLOCK_DESIGN_HEIGHT } from '../Components/Event/UnlockPremiumBanner';
import { HEADER_BACK_ROW_HEIGHT } from '../Components/Top/TopSection';
import { useHeaderTopOffset } from '../Pages/headerOffset';
import { MainLayout } from '../Pages/MainLayout';

/**
 * Event dashboard - Figma "NEW / Event Dashboard / From image brief" (3324:18828) on the
 * PAVEL DRAFT page. The brief is one tall mobile frame with its own scroll states ("01 Before
 * scroll" / "02 Scroll end"), so the whole column scrolls under the app's header and footer,
 * which MainLayout already supplies.
 *
 * There is no event feed on the backend yet: the numbers come from `createPlaceholderEvent` and
 * claiming only settles locally, the way DailyMissionsModal ships its placeholder mission list.
 */
// How far above the nav row the list starts dimming.
const FADE_LEAD_IN = 44;

const EventScreen = () => {
	const { t } = useTranslation();
	const { width, height, insets } = useSafeViewport();
	const { isDesktop } = useDevice();
	const desktop = useDesktopLayout();
	// The same backdrop MainLayout paints, so the fades match what is behind them.
	const backdrop = isDesktop ? BgLandscapePng : BgPortraitPng;

	const scale = Math.min(width / DESIGN_FRAME_WIDTH, MAX_SCALE);
	const contentWidth = DESIGN_CONTENT_WIDTH * scale;
	const gap = DESIGN_SECTION_GAP * scale;

	const [tab, setTab] = useState<EventTab>('rewards');
	const event = useMemo(() => createPlaceholderEvent(t), [t]);
	const [claimed, setClaimed] = useState({ free: false, premium: false });
	const [endsInLabel, setEndsInLabel] = useState(() => formatCountdown(event.endsAt - Date.now()));

	useEffect(() => {
		const tick = () => setEndsInLabel(formatCountdown(event.endsAt - Date.now()));
		tick();
		const interval = setInterval(tick, 1000);
		return () => clearInterval(interval);
	}, [event.endsAt]);

	const freeTrack: EventTrack = {
		...event.freeTrack,
		claimable: event.freeTrack.claimable && !claimed.free,
	};
	const premiumTrack: EventTrack = {
		...event.premiumTrack,
		claimable: event.premiumTrack.claimable && !claimed.premium,
	};
	const anythingToClaim = freeTrack.claimable || premiumTrack.claimable;

	const nextMilestone = [...event.freeTrack.milestones]
		.sort((a, b) => a - b)
		.find(milestone => milestone > event.points);

	const handleClaim = (track: EventTrack) =>
		setClaimed(current => ({ ...current, [track.id]: true }));

	const handleClaimAll = () => setClaimed({ free: true, premium: true });

	// The screen is hosted full-bleed, so the header and the nav float over the list and the
	// clearance both ends is this screen's to keep: the list runs edge to edge underneath them
	// and only its padding stops content from being permanently hidden.
	// This route draws the back-arrow header, so there are no missions / free-chips cards
	// overhanging the list - only the header row itself to clear.
	const headerSolid = useHeaderTopOffset() + HEADER_BACK_ROW_HEIGHT;
	const topClearance = headerSolid + gap;
	const bottomClearance = getNavRowHeight(width, height, true) + insets.bottom;
	// The banner rides a little above the nav rather than sitting on it.
	const bannerBottom = bottomClearance + gap;
	// Each fade is solid as far as its bar reaches and then eases out over a lead-in, so cards dim
	// on the way in instead of cutting off at the header's or the nav plate's edge.
	const bottomFadeHeight = bottomClearance + FADE_LEAD_IN;
	const topFadeHeight = headerSolid + FADE_LEAD_IN;
	// The banner floats over the list too, pinned just above the nav. The list reserves the
	// height of both at the end - otherwise the last card would stop underneath them.
	const bannerReserve = UNLOCK_DESIGN_HEIGHT * scale + bannerBottom + gap * 2;

	const tabContent = (columnWidth: number, unit: number) =>
		tab === 'rewards' ? (
			<>
				<EventTrackCard track={freeTrack} width={columnWidth} onClaim={handleClaim} />
				<EventTrackCard track={premiumTrack} width={columnWidth} onClaim={handleClaim} />

				<GreenButton
					label={t('T_SITE_EVENT_CLAIM_ALL', { defaultValue: 'CLAIM ALL' })}
					onPress={handleClaimAll}
					disabled={!anythingToClaim}
					width={columnWidth}
					height={36 * unit}
					fontSize={14 * unit}
					radius={8 * unit}
					borderWidth={Math.max(1, 1 * unit)}
				/>
			</>
		) : (
			<EventInfoPanel endsInLabel={endsInLabel} pointsGoal={event.pointsGoal} width={columnWidth} />
		);

	if (desktop.isDesktopLayout) {
		// The desktop frame in the desktop home's two columns: the event itself - banner, points
		// and the pass upsell - on the left, the tabs and what they show on the right. The mobile
		// blocks keep their own proportions, sized to the column (360 wide at 1.62 is the 1280
		// frame's 600-wide left column). The content box ends above the dock, so the upsell sits
		// in the column rather than pinned over it.
		const columns = getDesktopColumns(desktop.contentWidth, desktop.scale);
		const rightUnit = columns.right / DESIGN_CONTENT_WIDTH;
		const gapPx = desktop.s(16);

		return (
			<MainLayout fullBleed>
				<IndicatorScrollView
					style={[styles.scroll, desktopScrollFade(desktop.s(40))]}
					contentContainerStyle={[
						styles.desktopContent,
						{
							paddingHorizontal: desktop.side,
							paddingTop: desktop.s(8),
							paddingBottom: desktop.s(40),
							columnGap: columns.gap,
						},
					]}
					showsVerticalScrollIndicator={false}
				>
					<View style={{ width: columns.left, rowGap: gapPx }}>
						<EventHeroBanner
							kicker={event.kicker}
							title={event.title}
							description={event.description}
							endsInLabel={endsInLabel}
							width={columns.left}
						/>
						<EventPointsCard
							points={event.points}
							goal={event.pointsGoal}
							nextRewardIn={nextMilestone != null ? nextMilestone - event.points : 0}
							width={columns.left}
						/>
						<UnlockPremiumBanner
							price={event.passPrice}
							benefits={event.passBenefits}
							width={columns.left}
						/>
					</View>
					<View style={{ width: columns.right, rowGap: DESIGN_SECTION_GAP * rightUnit }}>
						<EventTabs value={tab} onChange={setTab} width={columns.right} />
						{tabContent(columns.right, rightUnit)}
					</View>
				</IndicatorScrollView>
			</MainLayout>
		);
	}

	return (
		<MainLayout fullBleed>
			<View style={styles.page}>
				<IndicatorScrollView
					style={styles.scroll}
					contentContainerStyle={[
						styles.scrollContent,
						{
							rowGap: gap,
							paddingTop: topClearance,
							paddingBottom: bannerReserve,
						},
					]}
					showsVerticalScrollIndicator={false}
					// The event runs under the header and the banner, cleared by padding; so does the bar -
					// 4 in from the screen edge, in the margin the centred column leaves.
					indicatorInsets={{ top: topClearance, bottom: bannerReserve, right: 4 }}
					reserveGutter={false}
				>
					<EventHeroBanner
						kicker={event.kicker}
						title={event.title}
						description={event.description}
						endsInLabel={endsInLabel}
						width={contentWidth}
					/>

					<EventPointsCard
						points={event.points}
						goal={event.pointsGoal}
						nextRewardIn={nextMilestone != null ? nextMilestone - event.points : 0}
						width={contentWidth}
					/>

					<EventTabs value={tab} onChange={setTab} width={contentWidth} />

					{tab === 'rewards' ? (
						<>
							<EventTrackCard track={freeTrack} width={contentWidth} onClaim={handleClaim} />
							<EventTrackCard track={premiumTrack} width={contentWidth} onClaim={handleClaim} />

							<GreenButton
								label={t('T_SITE_EVENT_CLAIM_ALL', { defaultValue: 'CLAIM ALL' })}
								onPress={handleClaimAll}
								disabled={!anythingToClaim}
								width={contentWidth}
								height={36 * scale}
								fontSize={14 * scale}
								radius={8 * scale}
								borderWidth={Math.max(1, 1 * scale)}
							/>
						</>
					) : (
						<EventInfoPanel
							endsInLabel={endsInLabel}
							pointsGoal={event.pointsGoal}
							width={contentWidth}
						/>
					)}
				</IndicatorScrollView>

				<EventScrollFade
					edge='top'
					source={backdrop}
					viewportWidth={width}
					viewportHeight={height}
					height={topFadeHeight}
					solid={headerSolid}
				/>

				<EventScrollFade
					edge='bottom'
					source={backdrop}
					viewportWidth={width}
					viewportHeight={height}
					height={bottomFadeHeight}
					solid={bottomClearance}
				/>

				<View pointerEvents='box-none' style={[styles.pinned, { bottom: bannerBottom }]}>
					<UnlockPremiumBanner
						price={event.passPrice}
						benefits={event.passBenefits}
						width={contentWidth}
					/>
				</View>
			</View>
		</MainLayout>
	);
};

const styles = StyleSheet.create({
	page: {
		flex: 1,
		minHeight: 0,
		alignItems: 'center',
		width: '100%',
	},
	scroll: {
		flex: 1,
		minHeight: 0,
		width: '100%',
	},
	scrollContent: {
		alignItems: 'center',
	},
	desktopContent: {
		flexDirection: 'row',
		alignItems: 'flex-start',
	},
	// The pass upsell is pinned to the foot of the column rather than scrolled past. It floats
	// over the ScrollView instead of taking a row beside it, so the list keeps the full height
	// of the screen and its cards are cut off by nothing but the viewport edge; the content's
	// bottom padding is what keeps the last card clear of the banner.
	pinned: {
		position: 'absolute',
		left: 0,
		right: 0,
		alignItems: 'center',
	},
});

export default EventScreen;
